import { ThemeMode } from "@/types/theme-mode";

const THEME_KEY = "theme";

/** Electron preload exposes this; absent in pure browser (e.g. `vite` web archive). */
type ElectronThemeBridge = {
  toggle: () => Promise<boolean>;
  dark: () => Promise<void>;
  light: () => Promise<void>;
  system: () => Promise<boolean>;
  current: () => Promise<"dark" | "light" | "system">;
};

function getElectronThemeBridge(): ElectronThemeBridge | null {
  if (typeof window === "undefined") return null;
  const tm = (window as unknown as { themeMode?: ElectronThemeBridge }).themeMode;
  if (!tm || typeof tm.current !== "function") return null;
  return tm;
}

export interface ThemePreferences {
  system: ThemeMode;
  local: ThemeMode | null;
}

export async function getCurrentTheme(): Promise<ThemePreferences> {
  const localTheme = localStorage.getItem(THEME_KEY) as ThemeMode | null;
  const bridge = getElectronThemeBridge();

  if (!bridge) {
    const stored =
      localTheme === "light" || localTheme === "dark" || localTheme === "system" ? localTheme : null;
    if (stored === "system") {
      const dark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? true;
      return { system: dark ? "dark" : "light", local: localTheme };
    }
    if (stored === "light") return { system: "light", local: localTheme };
    if (stored === "dark") return { system: "dark", local: localTheme };
    return { system: "dark", local: localTheme };
  }

  const currentTheme = await bridge.current();
  return {
    system: currentTheme,
    local: localTheme,
  };
}

export async function setTheme(newTheme: ThemeMode) {
  const bridge = getElectronThemeBridge();

  if (bridge) {
    switch (newTheme) {
      case "dark":
        await bridge.dark();
        updateDocumentTheme(true);
        break;
      case "light":
        await bridge.light();
        updateDocumentTheme(false);
        break;
      case "system": {
        const isDarkMode = await bridge.system();
        updateDocumentTheme(isDarkMode);
        break;
      }
    }
  } else {
    switch (newTheme) {
      case "dark":
        updateDocumentTheme(true);
        break;
      case "light":
        updateDocumentTheme(false);
        break;
      case "system": {
        const isDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
        updateDocumentTheme(isDarkMode);
        break;
      }
    }
  }

  localStorage.setItem(THEME_KEY, newTheme);
}

export async function toggleTheme() {
  const bridge = getElectronThemeBridge();
  if (bridge) {
    const isDarkMode = await bridge.toggle();
    const newTheme = isDarkMode ? "dark" : "light";
    updateDocumentTheme(isDarkMode);
    localStorage.setItem(THEME_KEY, newTheme);
    return;
  }
  const isDark = document.documentElement.classList.contains("dark");
  const nextDark = !isDark;
  updateDocumentTheme(nextDark);
  localStorage.setItem(THEME_KEY, nextDark ? "dark" : "light");
}

export async function syncThemeWithLocal() {
  try {
    const { local } = await getCurrentTheme();
    if (!local) {
      await setTheme("dark");
      return;
    }
    await setTheme(local);
  } catch (error) {
    console.warn("Theme sync failed, defaulting to dark theme:", error);
    updateDocumentTheme(true);
  }
}

function updateDocumentTheme(isDarkMode: boolean) {
  if (!isDarkMode) {
    document.documentElement.classList.remove("dark");
  } else {
    document.documentElement.classList.add("dark");
  }
}
